import { useEffect, type ReactNode } from 'react';
import { MechanicalAnalogDiagram } from './MechanicalAnalogDiagram';

function useRelayoutChartsOnPrint() {
  useEffect(() => {
    const relayout = () => window.dispatchEvent(new Event('resize'));
    window.addEventListener('beforeprint', relayout);
    window.addEventListener('afterprint', relayout);
    return () => {
      window.removeEventListener('beforeprint', relayout);
      window.removeEventListener('afterprint', relayout);
    };
  }, []);
}

function handleDownloadPdf() {
  window.dispatchEvent(new Event('resize'));
  requestAnimationFrame(() => window.print());
}

function Card({ className = '', children }: { className?: string; children: ReactNode }) {
  return (
    <div
      className={`rounded-xl border border-border bg-card print:border-0 print:bg-muted/30 print:shadow-none ${className}`}
    >
      {children}
    </div>
  );
}

function Badge({
  variant = 'outline',
  className = '',
  children,
}: {
  variant?: 'outline' | 'secondary' | 'primary';
  className?: string;
  children: ReactNode;
}) {
  const base = 'inline-flex items-center rounded-md px-2.5 py-0.5 text-xs font-medium';
  const variants = {
    outline: 'border border-border',
    secondary: 'bg-secondary text-secondary-foreground',
    primary: 'bg-primary text-primary-foreground',
  };
  return <span className={`${base} ${variants[variant]} ${className}`}>{children}</span>;
}

function Separator({ className = '' }: { className?: string }) {
  return <hr className={`border-0 border-t border-border print:border-gray-200 ${className}`} />;
}

const metrics = [
  { label: 'Close', value: '$127.41', color: 'green' },
  { label: '1-day return', value: '+9.21%', color: 'green' },
  { label: 'From prior close', value: '+$10.74 ($116.67)', color: 'green' },
  { label: '5-day bounce off Jun-25 low', value: '+18.8% from $107.27', color: 'green' },
  { label: 'vs ATH ($207.18, 2025-11-03)', value: '−38.5%', color: 'red' },
  { label: '20-day return', value: '−16.3%', color: 'red' },
  { label: 'RSI (14-day)', value: '~48 (neutral, not stretched)', color: 'amber' },
  { label: 'Today vs market', value: 'PLTR +9.2% while QQQ −1.5%', color: 'green' },
];

export function StockAnalogueReport() {
  useRelayoutChartsOnPrint();

  return (
    <article className="stock-analogue-report mx-auto max-w-3xl px-6 py-12 print:max-w-none print:px-0 print:py-0">
      <div className="mb-6 flex justify-end print:hidden">
        <button
          type="button"
          onClick={handleDownloadPdf}
          className="flex items-center gap-2 rounded-md border border-border bg-background px-4 py-2 text-sm transition-colors hover:bg-muted"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="6 9 6 2 18 2 18 9" />
            <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
            <rect x="6" y="14" width="12" height="8" />
          </svg>
          Download as PDF
        </button>
      </div>

      <header className="mb-8">
        <time className="text-sm text-muted-foreground mb-2 block">
          Wednesday, July 1, 2026 &mdash; PLTR / Palantir Technologies Inc.
        </time>
        <h1 className="text-4xl font-bold mb-4">
          {"Here's what happened today — and what history says next."}
        </h1>
      </header>

      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4">Quick outline of today</h2>
        <div className="space-y-5">
          <p className="text-lg leading-relaxed">
            PLTR closed at <strong>$127.41, up +9.21%</strong> (from $116.67), on ~46M shares — trading
            $119.35 to $128.25 on the day. What makes it stand out: <strong>PLTR ripped higher on a day
            the rest of tech fell hard.</strong> Semiconductors were dumped (Technology sector −2.57%,
            Nasdaq-100/QQQ −1.52%), yet PLTR — a <em>software</em> name — went the other way on its own
            news.
          </p>
          <p className="text-lg leading-relaxed">
            Today capped a <strong>5-session, +18.8% rebound</strong> off the June 25 low of $107.27.
            Even after this jump, PLTR is still <strong>~16% below where it traded 20 days ago</strong> and{' '}
            <strong>~38% below its all-time high of $207.18</strong> (Nov 3, 2025). Its momentum gauge
            (14-day RSI) sits at a neutral ~48 — recovered, but not stretched.
          </p>
        </div>
      </section>

      <Card className="p-6 mb-8 bg-muted/50">
        <h3 className="font-semibold mb-4">{"Today's PLTR snapshot:"}</h3>
        <div className="grid grid-cols-2 gap-3 mb-4">
          {metrics.map((m) => (
            <div key={m.label} className="flex items-center gap-2 flex-wrap">
              <span className="text-sm">{m.label}</span>
              <Badge
                variant="outline"
                className={
                  m.color === 'green'
                    ? 'bg-green-500/10 text-green-700 border-green-200'
                    : m.color === 'red'
                    ? 'bg-red-500/10 text-red-700 border-red-200'
                    : 'bg-amber-500/10 text-amber-700 border-amber-200'
                }
              >
                {m.value}
              </Badge>
            </div>
          ))}
        </div>
        <p className="text-sm text-muted-foreground">
          Relief rally inside a deep drawdown. Stock-specific catalyst on a day of broad chip profit-taking
          and positive market breadth (7 of 11 sectors rose).
        </p>
      </Card>

      <Separator className="my-8" />

      <section className="mb-12">
        <h2 className="text-2xl font-bold mb-6">How it happened</h2>
        <p className="text-muted-foreground mb-5">Two forces converged:</p>
        <div className="space-y-5">
          <div className="flex gap-3">
            <span className="text-red-500 font-bold mt-0.5 shrink-0">1.</span>
            <p className="leading-relaxed">
              <strong>The setup (an oversold washout):</strong> Through June, PLTR fell ~7 sessions
              straight — France&apos;s DGSI intelligence agency moving to replace Palantir&apos;s software
              (June 22, −6.98%), UK NHS/MoD scrutiny, a broad &ldquo;sell high-multiple software&rdquo;
              rotation, and insider selling. That flushed it to $107.27 on June 25 (deeply oversold).
            </p>
          </div>
          <div className="flex gap-3">
            <span className="text-emerald-600 font-bold mt-0.5 shrink-0">2.</span>
            <p className="leading-relaxed">
              <strong>The trigger (a fundamental catalyst + rotation):</strong> The rebound built on the
              Zeta Global 7-year deal and Michael Burry halving his short (June 26), an Nvidia
              &ldquo;Nemotron&rdquo; partnership (June 29), and Japan evaluating its Maven system (June
              30). Today&apos;s move is attributed to{' '}
              <strong>management raising full-year revenue guidance to $7.65–$7.66B (~71% year-over-year
              growth).</strong> Critically, money was rotating <em>out of AI chips and into AI software</em>{' '}
              today — PLTR was a direct beneficiary of the exact rotation that was hammering semis.
            </p>
          </div>
        </div>
        <p className="mt-5 text-sm text-muted-foreground leading-relaxed">
          <strong className="text-foreground">Note:</strong> The +9.21% price move is hard data from the
          OHLC feed; the guidance figure is the driver recorded by internal stock-move analysis, not an
          externally verified news item like the earlier catalysts.
        </p>
      </section>

      <Separator className="my-8" />

      <section className="mb-12">
        <h2 className="text-2xl font-bold mb-8">
          {"This stock has been here before. Here's what history says."}
        </h2>

        <div className="mb-10">
          <h3 className="text-xl font-semibold mb-3 flex items-center gap-2 flex-wrap">
            Closest situational analog:
            <Badge variant="secondary">2026-05-29 (+9.21%)</Badge>
          </h3>
          <p className="mb-3 text-muted-foreground">
            Same type of trigger — a big fundamental pop on company-specific news
          </p>
          <p className="mb-4 leading-relaxed">
            The best event-type twin is barely a month old: on <strong>May 29, 2026, PLTR also rose
            exactly +9.21%</strong>, driven by record Q1 results, a raised outlook, and AI-partner
            validation (Dell/Snowflake). Same stock, same kind of &ldquo;great fundamental news → big
            single-day pop,&rdquo; same magnitude to the decimal.
          </p>
          <div className="flex gap-3 mb-4">
            <span className="text-red-500 font-bold mt-0.5 shrink-0">&#8595;</span>
            <p>
              <strong>What happened next was a warning:</strong> the pop was completely sold. +3 days
              −9.2%, +5 days −13.4%, +10 days −18.2%, <strong>+20 days −26.1%</strong> — as valuation
              worries and the France/UK contract-loss headlines took over.
            </p>
          </div>
          <div className="flex gap-3">
            <span className="text-emerald-600 font-bold mt-0.5 shrink-0">&#8593;</span>
            <p>
              <strong>Key difference that softens the warning:</strong> May 29&apos;s pop came from an{' '}
              <em>already-extended, near-the-highs</em> position ($156.54, priced for perfection).
              Today&apos;s pop comes from a <em>beaten-down</em> base (−38% off the peak, neutral
              momentum). So today is less vulnerable to a &ldquo;priced for perfection&rdquo; unwind than
              May 29 was.
            </p>
          </div>
        </div>

        <div className="mb-10">
          <h3 className="text-xl font-semibold mb-3 flex items-center gap-2 flex-wrap">
            Closest mechanical analog:
            <Badge variant="secondary">2022-03-16 (+9.47%)</Badge>
          </h3>
          <p className="mb-3 text-muted-foreground">
            Same price shape — a ~+9% single-day jump while still down ~16–17% over the prior 20 days
          </p>
          <p className="mb-4 leading-relaxed">
            Filtering purely on price <em>shape</em> — a sharp bounce inside a downtrend with momentum
            back near neutral — the nearest match in PLTR&apos;s entire history is{' '}
            <strong>March 16, 2022 (+9.47%)</strong>. That day was a broad tech relief rally after a
            February plunge to then-record lows (cause differs, which is fine for a shape match).
          </p>

          <Card className="p-5 bg-muted/40 mb-5">
            <MechanicalAnalogDiagram />
          </Card>

          <div className="flex gap-3">
            <span className="text-amber-500 font-bold mt-0.5 shrink-0">&#9679;</span>
            <p>
              <strong>What happened next:</strong> the bounce <em>extended first, then failed.</em> +5
              days +11.8%, +10 days +17.7%, +20 days +9.4% — but <strong>+40 days −37.7%</strong>. The
              shape historically delivered a tradeable 2–4 week rally before the underlying downtrend
              reasserted.
            </p>
          </div>
        </div>

        <div className="mb-6">
          <h3 className="text-xl font-semibold mb-3 flex items-center gap-2 flex-wrap">
            Closest regime / macro analog:
            <Badge variant="secondary">2025-07-24</Badge>
          </h3>
          <p className="mb-3 text-muted-foreground">
            Same macro backdrop as today&apos;s market — from the July 1, 2026 market analogue file
          </p>
          <p className="mb-4 leading-relaxed">
            The macro backdrop today (10-yr yield ~4.4–4.5%, normal curve, VIX ~15–16, ~+8–9% YTD,
            &ldquo;resilient&rdquo; fragility, Fed on hold and data-dependent into summer) matches{' '}
            <strong>July 24, 2025</strong>. That regime produced a <strong>choppy sideways grind</strong>{' '}
            — S&P +0.1%, Nasdaq −0.5% over the next 20 sessions — with no trend until a fresh dovish
            catalyst arrived (Jackson Hole, late August). Tech went nowhere (−0.9%).
          </p>
          <p className="mb-4 leading-relaxed">
            <strong>What PLTR actually did during that regime:</strong> On July 24, 2025 PLTR closed at
            $154.86. For the first ~10 days the tape matched the macro script exactly — drift, chop,
            nothing resolved. Then Q2 earnings arrived on <strong>August 5</strong>: the company&apos;s
            first-ever $1B revenue quarter (+93% U.S. commercial growth year-over-year). PLTR surged
            +7.85% that day and continued grinding higher to <strong>+20.7% by day 14</strong> (August
            8). But the gains evaporated fast: a Citron Research bearish report hit on August 19 arguing
            the stock was detached from fundamentals, sending it <strong>−9.35% in a single session</strong>.
            By day 20 PLTR was essentially flat vs. July 24 — only <strong>+0.9%</strong>.
          </p>
          <div className="flex gap-3">
            <span className="text-amber-500 font-bold mt-0.5 shrink-0">&#9679;</span>
            <p className="leading-relaxed">
              <strong>The lesson:</strong> absent a company-specific catalyst, this macro regime just
              drifts. When a catalyst <em>does</em> arrive (earnings, guidance, a bearish short report),
              it moves PLTR sharply — but the moves cancel out. The regime analog says: the next 3–4
              weeks likely churn sideways on macro alone. The <em>direction</em> will be decided by
              company-specific news, not the tape.
            </p>
          </div>
        </div>
      </section>

      <Separator className="my-8" />

      <section className="mb-12">
        <h2 className="text-2xl font-bold mb-6">
          Overall: what the analogs say about PLTR&apos;s direction
        </h2>
        <p className="mb-4 leading-relaxed">
          The three lenses line up on a <strong>cautious, &ldquo;sell-the-rip until proven
          otherwise&rdquo;</strong> read for the next 2–4 weeks:
        </p>
        <ul className="mb-5 space-y-2 pl-5 list-disc leading-relaxed">
          <li>
            <strong>Situational (May 29):</strong> the <em>identical</em> +9% fundamental pop fully
            round-tripped within weeks.
          </li>
          <li>
            <strong>Mechanical (Mar 2022):</strong> the <em>shape</em> gave a couple of good weeks up,
            then rolled over hard.
          </li>
          <li>
            <strong>Regime (Jul 2025):</strong> the <em>macro</em> wants to chop sideways with no
            durable trend absent a new catalyst.
          </li>
        </ul>
        <p className="mb-4 leading-relaxed">
          Near-term, momentum could carry PLTR a bit higher (both twins extended for days-to-weeks first).
          But the 20–40 day picture across all three is <strong>flat-to-negative</strong>.
        </p>
        <div className="pl-4 border-l-2 border-border">
          <p className="leading-relaxed">
            <strong>The bull escape hatch is real, though:</strong> the one analog that matched &ldquo;big
            fundamental pop <em>off</em> an oversold washout&rdquo; — <strong>Aug 6, 2024 (+10.38% post-earnings,
            RSI 22)</strong> — kept going (+21% at 10 days, +41% at 40 days). The deciding factor is
            whether today&apos;s guidance is a <em>durable</em> re-rating (like Aug 2024) or{' '}
            <em>sentiment that fades</em> (like May 29). Right now the evidence leans toward &ldquo;prove
            it&rdquo;: treat this as a rally to respect but not chase, with a genuine bottom only
            confirmed if the guidance sticks and the European contract overhang doesn&apos;t worsen.
          </p>
        </div>
      </section>

      <Separator className="my-8" />

      <section className="mb-12">
        <h2 className="text-2xl font-bold mb-6">3 situations / risks to monitor</h2>
        <div className="space-y-10">
          <div>
            <h3 className="text-xl font-semibold mb-3 flex items-center gap-2">
              <Badge className="bg-primary text-primary-foreground">1</Badge>
              The France/UK &ldquo;digital sovereignty&rdquo; overhang
            </h3>
            <div className="pl-10">
              <p className="leading-relaxed">
                This is the <em>exact</em> thing that killed the May 29 pop. Any new European government
                defection or NHS/MoD escalation would flip today&apos;s guidance story back into a
                valuation-and-contract-loss selloff.
              </p>
            </div>
          </div>
          <div>
            <h3 className="text-xl font-semibold mb-3 flex items-center gap-2">
              <Badge className="bg-primary text-primary-foreground">2</Badge>
              Rates + valuation
            </h3>
            <div className="pl-10">
              <p className="leading-relaxed">
                The 10-year yield is 4.48%, right under the 4.5% &ldquo;stress line.&rdquo; PLTR (trading
                ~40×-plus forward revenue) is among the most rate-/valuation-sensitive names there is. If
                yields push over 4.5%, high-multiple software gets hit first. Also watch the chips→software
                rotation that <em>helped</em> today — it can reverse just as fast.
              </p>
            </div>
          </div>
          <div>
            <h3 className="text-xl font-semibold mb-3 flex items-center gap-2">
              <Badge className="bg-primary text-primary-foreground">3</Badge>
              Gap-fill / follow-through
            </h3>
            <div className="pl-10">
              <p className="leading-relaxed">
                PLTR gapped up over ~$120. Watch whether it <em>holds</em> that breakout on volume. A
                close back below ~$120 (filling today&apos;s gap) would rhyme with the May 29 → June
                collapse; holding above it would favor the Aug-2024 durable-bottom path.
              </p>
            </div>
          </div>
        </div>
      </section>

      <Separator className="my-8" />

      <section className="mb-12">
        <h2 className="text-2xl font-bold mb-5">The most dangerous upcoming window</h2>
        <p className="text-muted-foreground mb-6">
          <strong>July 2 Nonfarm Payrolls (8:30 a.m. ET) into the July 3 market holiday and July 4
          weekend.</strong>
        </p>
        <div className="space-y-5">
          <div className="flex gap-3">
            <span className="text-red-500 font-bold mt-0.5 shrink-0">1.</span>
            <p className="leading-relaxed">
              It&apos;s a Tier-1 jobs report (forecast ~110K vs 172K prior), and the ADP preview{' '}
              <em>already missed</em> at 98K.
            </p>
          </div>
          <div className="flex gap-3">
            <span className="text-red-500 font-bold mt-0.5 shrink-0">2.</span>
            <p className="leading-relaxed">
              It&apos;s the <strong>last full session before a long, thin-liquidity holiday</strong> —
              thin volume amplifies whatever direction the number pushes.
            </p>
          </div>
          <div className="flex gap-3">
            <span className="text-red-500 font-bold mt-0.5 shrink-0">3.</span>
            <p className="leading-relaxed">
              A <strong>hot</strong> print sends the 10-year over 4.5% → a direct hit to a high-multiple
              name that just gapped +9%. A <strong>clear miss</strong> eases yields and supports the
              rebound.
            </p>
          </div>
        </div>
        <p className="mt-6 leading-relaxed text-muted-foreground">
          The danger is specifically that PLTR is entering an illiquid, top-tier-data window{' '}
          <em>right after</em> a euphoric single-day pop — the same configuration that reversed after May
          29. (PLTR&apos;s own next big single-stock event, Q2 earnings in early August, sits just beyond
          this window and is the next major test of whether today&apos;s guidance holds.)
        </p>
      </section>

      <Separator className="my-8" />

      <footer className="text-center py-8">
        <p className="text-xl font-semibold mb-3">
          Three analogues. Two cautionary. One sideways.
        </p>
        <p className="text-lg text-muted-foreground mb-2">
          The guidance raise is real. So is the valuation debate, the broken chart, and July 2 NFP.
        </p>
        <p className="text-muted-foreground">
          Watch ~$120 on volume. Watch for European contract headlines. Watch the 10-year yield at 4.48%.
        </p>
      </footer>
    </article>
  );
}
